import { UserRole } from '@/core/generated/api';
import type {
    ChatParticipant,
    ChatConversation,
    ChatMessage,
    ChatRole,
    ChatSummary,
    ChatTimestamp,
    UserChatSummary,
} from './types';

const CLIENT_ROLE: ChatRole = 'Client';
const EXECUTOR_ROLE: ChatRole = 'Executor';

/** Преобразует роль собеседника Firestore в роль API */
export const mapChatRoleToApiRole = (role: ChatRole): UserRole => {
    switch (role) {
        case 'Client':
            return UserRole.Client;
        case 'Executor':
            return UserRole.Executor;
    }
};

/** Проверяет, является ли запись чата объектом */
const isRecord = (value: unknown): value is Record<string, unknown> => (
    typeof value === 'object' && value !== null && !Array.isArray(value)
);

/** Читает обязательное текстовое значение чата */
const readRequiredString = (value: unknown): string | null => {
    if (typeof value !== 'string') return null;

    const normalized = value.trim();

    return normalized.length > 0
        ? normalized
        : null;
};

/** Читает необязательное текстовое значение чата */
const readOptionalString = (value: unknown, fallback = ''): string => (
    typeof value === 'string'
        ? value
        : fallback
);

/** Читает адреса фотографий сообщения */
const readPhotoUrls = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];

    return value.reduce<string[]>((urls, item) => {
        const url = readRequiredString(item);

        if (url) urls.push(url);
        return urls;
    }, []);
};

/** Читает количество непрочитанных сообщений */
const readUnreadCount = (value: unknown): number => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0;
    return Math.floor(value);
};

/** Проверяет отметку времени сообщения */
const readValidTimestamp = (value: number): ChatTimestamp | null => (
    Number.isFinite(value) && !Number.isNaN(new Date(value).getTime())
        ? value
        : null
);

/** Приводит значение роли из Firestore к роли чата поддержки */
export const normalizeChatRole = (value: unknown): ChatRole | null => {
    if (typeof value !== 'string') return null;

    switch (value.trim().toLocaleLowerCase('en-US')) {
        case 'client':
            return CLIENT_ROLE;
        case 'executor':
            return EXECUTOR_ROLE;
        default:
            return null;
    }
};

/** Преобразует Firestore Timestamp или совместимое значение в Unix-время */
export const mapFirestoreTimestamp = (value: unknown): ChatTimestamp | null => {
    if (value instanceof Date) {
        return readValidTimestamp(value.getTime());
    }

    if (typeof value === 'number') {
        return readValidTimestamp(value);
    }

    if (typeof value === 'string') {
        return readValidTimestamp(Date.parse(value));
    }

    if (!isRecord(value) || typeof value.seconds !== 'number' || !Number.isFinite(value.seconds)) {
        return null;
    }

    const nanoseconds = typeof value.nanoseconds === 'number' && Number.isFinite(value.nanoseconds)
        ? value.nanoseconds
        : 0;

    return readValidTimestamp((value.seconds * 1_000) + (nanoseconds / 1_000_000));
};

/** Преобразует данные собеседника в модель чата */
const mapChatCompanion = (value: unknown): ChatParticipant | null => {
    if (!isRecord(value)) return null;

    const id = readRequiredString(value.id);
    const role = normalizeChatRole(value.role) ?? readRequiredString(value.role);

    if (!id || !role) return null;

    return {
        id,
        role,
        displayNameRole: readRequiredString(value.displayNameRole) ?? role,
        name: readRequiredString(value.name) ?? 'Неизвестный',
        photoUrl: readRequiredString(value.photoUrl),
    };
};

/** Преобразует запись в краткие данные чата */
const mapChatSummary = (value: unknown): UserChatSummary | null => {
    if (!isRecord(value)) return null;

    const id = readRequiredString(value.id);
    const companion = mapChatCompanion(value.companion);

    if (!id || !companion) return null;

    const unreadCount = readUnreadCount(value.notViewedCount);

    return {
        id,
        companion,
        lastMessage: readOptionalString(value.lastMessage),
        lastMessageAt: mapFirestoreTimestamp(value.lastSendMessage),
        isViewed: typeof value.isViewed === 'boolean'
            ? value.isViewed
            : unreadCount === 0,
        unreadCount,
        hasMessages: Boolean(readRequiredString(value.lastMessageId) || readRequiredString(value.lastPhotoUrl) || readRequiredString(value.lastMessage)),
    };
};

/** Преобразует документ пользователя в список его переписок */
export const mapUserChats = (data: unknown): UserChatSummary[] => {
    const source = Array.isArray(data)
        ? data
        : isRecord(data) && Array.isArray(data.chats)
            ? data.chats
            : [];

    return source.reduce<UserChatSummary[]>((chats, item) => {
        const chat = mapChatSummary(item);

        if (chat) chats.push(chat);
        return chats;
    }, []);
};

/** Выбирает диалоги поддержки с клиентами и исполнителями */
export const mapSupportChats = (data: unknown): ChatSummary[] => (
    mapUserChats(data)
        .flatMap((chat) => {
            const role = normalizeChatRole(chat.companion.role);

            return role
                ? [{
                    ...chat,
                    companion: {
                        ...chat.companion,
                        role
                    }
                }]
                : [];
        })
);

/** Преобразует запись в сообщение выбранного чата */
const mapChatMessage = (
    conversationId: string,
    value: unknown,
    index: number,
): ChatMessage | null => {
    if (!isRecord(value)) return null;

    const createdAt = mapFirestoreTimestamp(value.created);

    if (createdAt === null) return null;

    const message = readOptionalString(value.message);
    const photoUrls = readPhotoUrls(value.photoUrls);

    if (message.trim().length === 0 && photoUrls.length === 0) return null;

    return {
        id: readRequiredString(value.id) ?? `${conversationId}:message:${index}`,
        message,
        photoUrls,
        isMine: value.isMine === true,
        isViewed: value.isViewed === true,
        createdAt,
    };
};

/** Преобразует данные документа Firestore в диалог поддержки */
export const mapChatConversation = (
    id: string,
    data: unknown,
): ChatConversation | null => {
    const conversationId = readRequiredString(id);

    if (!conversationId || !isRecord(data)) return null;

    const sourceMessages = Array.isArray(data.messages)
        ? data.messages
        : [];

    const messages = sourceMessages.reduce<ChatMessage[]>((result, item, index) => {
        const message = mapChatMessage(conversationId, item, index);

        if (message) result.push(message);
        return result;
    }, []);

    return {
        id: conversationId,
        messages,
        note: readOptionalString(data.note),
    };
};
